import { useEffect, useMemo, useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { useMountEffect } from '@react-hookz/web';
import { useHistory, useLocation, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { css } from 'styled-components';

import { AnalyticSources } from 'src/app/analytics/analyticsEvents';
import Spinner from 'src/app/components/Elements/Spinner';
import { useGetOrder, useOrderCount } from 'src/app/hooks/data/order';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { orders } from 'src/app/router/paths';
import { loadHomescreenMedications } from 'src/app/state/actions/homescreen';
import { getAccountSignupSource, getHasPrimaryPatientEverTransferred } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import type { Order, SegmentEvent } from 'src/app/types';
import { isHydra } from 'src/app/utils/browser';
import { DeliveryMethodType, ORDER_STATUSES } from 'src/consts';

import { TopHalfOfOrderDetails } from './OrderStatus';
import { OrderDetailCards } from './SummaryDetails';
import { getSegmentOrderStatus } from './utils';

interface TrackEventViewOrderTrackingProps {
  order: Order;
  isFirstPageViewedInApp: boolean;
  experiment: string | null;
}

const TrackEventViewOrderTracking = ({
  order,
  isFirstPageViewedInApp,
  experiment,
}: TrackEventViewOrderTrackingProps) => {
  const { pathname: path } = useLocation();

  useSegmentEvent('viewOrderTracking', {
    shouldTrackOnMount: true,
    payload: {
      order_id: order.id,
      order_status: getSegmentOrderStatus(order.status, order.deliveryMethod?.key),
      source: isFirstPageViewedInApp ? 'Deeplink' : 'Organic_Navigation',
      path,
      experiment,
    },
  });

  return null;
};

const TrackEligibleForMCYPExperience = () => {
  useSegmentEvent('viewMCYPExperienceIsEligible', {
    shouldTrackOnMount: true,
  });

  return null;
};

const TrackEventViewFailedDeliveryReschedule = () => {
  const { orderId } = useParams<{ orderId: string }>();

  useSegmentEvent('viewFailedDeliveryReschedule', {
    shouldTrackOnMount: true,
    payload: { source: AnalyticSources.orderDetails, order_id: orderId },
  });

  return null;
};

const MobileHardcodedProgressBar = () => (
  <div
    aria-hidden
    css={css`
      ${({ theme }) => theme.mediaQueries.small} {
        display: none;
      }

      position: absolute;
      left: 0;
      right: 0;
      top: ${({ theme }) => theme.space.loggedInHeaderNavHeight};
      bottom: unset;
      height: 0.8rem;
      background-color: ${({ theme }) => theme.colors.capsuleGreen10};
      width: 100%;
    `}
  >
    <div
      css={css`
        width: 75%;
        height: 100%;
        background-color: ${({ theme }) => theme.colors.capsuleGreen80};
        border-top-right-radius: ${({ theme }) => theme.radii.r300};
        border-bottom-right-radius: ${({ theme }) => theme.radii.r300};
      `}
    />
  </div>
);

interface OrderTrackingRendererProps {
  dispatchSegmentTrack: SegmentEvent;
  previousPathname: string;
}

export const OrderTrackingRenderer = ({ dispatchSegmentTrack, previousPathname }: OrderTrackingRendererProps) => {
  const { orderId } = useParams<{ orderId: string }>();
  const location = useLocation();
  const { replace } = useHistory();

  const dispatch = useDispatch();
  const orderTrackingPollingInterval = 360000;

  const {
    data: order,
    isLoading: isLoadingOrder,
    isError: hasGetOrderError,
  } = useGetOrder(orderId, {
    refetchInterval: orderTrackingPollingInterval,
  });

  const orderStatus = order?.status;

  // MCYP experiment evaluation. This used to be an imported hook but it's clearer to inline it here
  const [shouldShowMCYPExperienceVariant, setShouldShowMCYPExperienceVariant] = useState<string | null>(null);
  const shouldShowMCYPExperience = useMemo(
    () => shouldShowMCYPExperienceVariant === 'show_experience',
    [shouldShowMCYPExperienceVariant]
  );
  const [isLoadingMCYPExperiment, setIsLoadingMCYPExperiment] = useState<boolean>(true);
  const { fetchVariant } = useExperiments();
  const { loggedInUser, isLoggedIn } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', isLoggedIn);
  const acquisitionSource = useSelector(getAccountSignupSource);
  const { data: ordersPlaced } = useOrderCount();
  const hasEverTransferred = useSelector(getHasPrimaryPatientEverTransferred);
  useEffect(() => {
    setIsLoadingMCYPExperiment(true);
    if (!!user) {
      const evaluateMCYPExperiment = async () => {
        const variant = await fetchVariant({
          experimentKey: 'mcyp_show_experience',
          userId: user.accountId,
          audienceAttributes: {
            /*
             * Audience eligibility is determined in Amplitude
             * As of 06/27/2024, the user is eligible if:
             *   - They have never transferred a medication
             *   - They have placed at least one order but fewer than 5
             *   - The order status is 'received'
             */
            has_ever_transferred: hasEverTransferred,
            orders_placed: ordersPlaced,
            order_status: orderStatus,
          },
        });

        setShouldShowMCYPExperienceVariant(!isHydra() ? variant : null);
        setIsLoadingMCYPExperiment(false);
      };
      evaluateMCYPExperiment();
    }
  }, [
    setShouldShowMCYPExperienceVariant,
    user,
    fetchVariant,
    acquisitionSource,
    hasEverTransferred,
    orderStatus,
    ordersPlaced,
  ]);
  // End MCYP experiment evaluation

  useMountEffect(() => {
    dispatch(loadHomescreenMedications());
  });

  useEffect(() => {
    if (orderStatus === ORDER_STATUSES.canceled || hasGetOrderError) {
      replace(orders.route);
    }
  }, [hasGetOrderError, orderStatus, replace]);

  const isLoading = isLoadingMCYPExperiment || isLoadingOrder;
  const isFirstPageViewedInApp = previousPathname === location?.pathname;
  const isOrderPostponed = orderStatus === ORDER_STATUSES.postponed;
  const isCourierOrder = order?.deliveryMethod?.key === DeliveryMethodType.courier;
  const courierDeliveryMethod = isCourierOrder ? order?.deliveryMethod : null;

  let shouldShowCourierMap = false;
  if (!!courierDeliveryMethod) {
    const isOrderDelivering = orderStatus === ORDER_STATUSES.delivering;
    const hasCourierCoordinates = 'courier' in courierDeliveryMethod && !!courierDeliveryMethod?.courier?.coordinates;
    const hasDelayedDelivery = 'isDeliveryDelayed' in courierDeliveryMethod && courierDeliveryMethod?.isDeliveryDelayed;

    shouldShowCourierMap = hasCourierCoordinates && isOrderDelivering && !hasDelayedDelivery;
  }

  if (isLoading || !order) {
    return <Spinner centered />;
  }

  return (
    <>
      {shouldShowMCYPExperience && <MobileHardcodedProgressBar />}
      {
        /* If variant is not null, patient is eligible */
        shouldShowMCYPExperienceVariant && <TrackEligibleForMCYPExperience />
      }

      {!isLoadingMCYPExperiment && (
        <TrackEventViewOrderTracking
          order={order}
          isFirstPageViewedInApp={isFirstPageViewedInApp}
          experiment={shouldShowMCYPExperience ? 'pots-continue-button' : null}
        />
      )}
      {isOrderPostponed && <TrackEventViewFailedDeliveryReschedule />}
      <Typography
        variant="body"
        css={css`
          &:hover {
            text-decoration: underline;
          }
          ${({ theme }) => theme.mediaQueries.smallDown} {
            display: none;
          }
        `}
      >
        <Link to={orders.toPath()}>&larr; Your orders</Link>
      </Typography>
      {!isOrderPostponed && <TopHalfOfOrderDetails order={order} shouldShowCourierMap={shouldShowCourierMap} />}
      <OrderDetailCards
        order={order}
        dispatchSegmentTrack={dispatchSegmentTrack}
        shouldShowCourierMap={shouldShowCourierMap}
      />
    </>
  );
};
